import { useEffect, useRef } from 'react';
import { useStores } from '@/contexts/StoreContext';
import { useHistory } from '@/hooks/useHistory';
// import { toolManager } from '@/tools/ToolManager';
// import { SelectTool } from '@/tools/SelectTool';

export const useKeyboardShortcuts = () => {
  const { projectStore, uiStore } = useStores();
  const { canUndo, canRedo, undo, redo } = useHistory();

  // Debounce mechanism to prevent double-calls
  const lastPasteTime = useRef<number>(0);
  const PASTE_DEBOUNCE_MS = 100;

  // Handle SVG paste from clipboard
  const handleClipboardPaste = async (): Promise<boolean> => {
    const now = Date.now();
    if (now - lastPasteTime.current < PASTE_DEBOUNCE_MS) {
      return false;
    }
    lastPasteTime.current = now;

    try {
      // First try to read clipboard items (for images)
      try {
        const clipboardItems = await navigator.clipboard.read();

        for (const clipboardItem of clipboardItems) {
          // Try to handle images first (PNG, JPEG, etc.)
          for (const type of clipboardItem.types) {
            if (type.startsWith('image/')) {
              const blob = await clipboardItem.getType(type);
              const reader = new FileReader();

              return new Promise(resolve => {
                reader.onload = () => {
                  const imageData = reader.result as string;
                  const centerX = 400;
                  const centerY = 300;

                  // Create an Image object to get dimensions
                  const img = new Image();
                  img.onload = () => {
                    const maxSize = 300;
                    let width = img.width;
                    let height = img.height;

                    // Scale down if too large
                    if (width > maxSize || height > maxSize) {
                      const ratio = Math.min(maxSize / width, maxSize / height);
                      width *= ratio;
                      height *= ratio;
                    }

                    const imageElement = {
                      type: 'image' as const,
                      x: centerX - width / 2,
                      y: centerY - height / 2,
                      width,
                      height,
                      imageData,
                      style: {
                        strokeColor: '#000',
                        strokeWidth: 1,
                        strokeStyle: 'solid' as const,
                        fillColor: 'transparent',
                        fillPattern: 'solid' as const,
                        opacity: 1,
                        fontSize: 16,
                        fontFamily: 'sans-serif',
                        textAlign: 'center' as const,
                        textVerticalAlign: 'middle' as const,
                        textType: 'sloppy' as const,
                      },
                      isSelected: false,
                    };

                    projectStore.createElement(imageElement);
                    // Switch to select tool after pasting
                    uiStore.setActiveTool('select');
                    resolve(true);
                  };
                  img.src = imageData;
                };

                reader.onerror = () => resolve(false);
                reader.readAsDataURL(blob);
              });
            }
          }
        }
      } catch (imageError) {
        // Fall through to text handling if image reading fails
      }

      // If no image found, try text content
      const text = await navigator.clipboard.readText();

      // Check if the clipboard contains SVG content
      if (text.trim().startsWith('<svg') || text.includes('</svg>')) {
        const centerX = 400;
        const centerY = 300;

        // Extract width and height from SVG if available
        const svgWidthMatch = text.match(/width\s*=\s*["']?(\d+(?:\.\d+)?)/i);
        const svgHeightMatch = text.match(/height\s*=\s*["']?(\d+(?:\.\d+)?)/i);
        const svgViewBoxMatch = text.match(
          /viewBox\s*=\s*["']?[^"']*?\s+([^"'\s]+)\s+([^"'\s]+)["']?/i
        );

        let width = 100;
        let height = 100;

        if (
          svgWidthMatch &&
          svgHeightMatch &&
          svgWidthMatch[1] &&
          svgHeightMatch[1]
        ) {
          width = Math.min(parseFloat(svgWidthMatch[1]), 300);
          height = Math.min(parseFloat(svgHeightMatch[1]), 300);
        } else if (
          svgViewBoxMatch &&
          svgViewBoxMatch[1] &&
          svgViewBoxMatch[2]
        ) {
          width = Math.min(parseFloat(svgViewBoxMatch[1]), 300);
          height = Math.min(parseFloat(svgViewBoxMatch[2]), 300);
        }

        const svgElement = {
          type: 'svg' as const,
          x: centerX - width / 2,
          y: centerY - height / 2,
          width,
          height,
          svgContent: text,
          style: {
            strokeColor: '#000',
            strokeWidth: 1,
            strokeStyle: 'solid' as const,
            fillColor: 'transparent',
            fillPattern: 'solid' as const,
            opacity: 1,
            fontSize: 16,
            fontFamily: 'sans-serif',
            textAlign: 'center' as const,
            textVerticalAlign: 'middle' as const,
            textType: 'sloppy' as const,
          },
          isSelected: false,
        };

        projectStore.createElement(svgElement);
        // Switch to select tool after pasting
        uiStore.setActiveTool('select');
        return true;
      }

      // If not SVG and we have plain text, create a text element (but not if it's empty or just whitespace)
      if (text && text.trim().length > 0 && text.trim().length < 1000) {
        // Reasonable text length limit
        const centerX = 400;
        const centerY = 300;

        // Calculate approximate text dimensions
        const fontSize = uiStore.persistentStyle.fontSize || 16;
        const textContent = text.trim();
        const estimatedWidth = Math.max(
          100,
          textContent.length * fontSize * 0.6
        ); // rough estimate
        const estimatedHeight = Math.max(20, fontSize * 1.2); // line height

        // Get current text style from UI store or use defaults
        const textElement = {
          type: 'text' as const,
          x: centerX - estimatedWidth / 2,
          y: centerY - estimatedHeight / 2,
          width: estimatedWidth,
          height: estimatedHeight,
          text: textContent,
          style: {
            ...uiStore.persistentStyle,
            fillColor: uiStore.persistentStyle.fillColor || '#000', // For text, fill is important
          },
          isSelected: false,
        };

        projectStore.createElement(textElement);
        // Switch to select tool after pasting
        uiStore.setActiveTool('select');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      return false;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't handle shortcuts if user is typing in an input or editing text
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        uiStore.editingTextId !== null
      ) {
        return;
      }

      // Prevent default browser behavior for our shortcuts
      const shortcutKeys = [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        'Delete',
        'Backspace',
        'Escape',
        ' ', // Space
      ];

      // Handle Ctrl/Cmd combinations separately to prevent browser defaults
      const isCtrlOrCmd = event.ctrlKey || event.metaKey;
      const ctrlShortcutKeys = ['z', 'y', 'a', 'c', 'v', 'd', 'g'];

      if (shortcutKeys.includes(event.key)) {
        event.preventDefault();
      } else if (
        isCtrlOrCmd &&
        ctrlShortcutKeys.includes(event.key.toLowerCase())
      ) {
        event.preventDefault();
      }

      // Handle Ctrl/Cmd combinations FIRST (highest priority)
      if (isCtrlOrCmd) {
        switch (event.key.toLowerCase()) {
          case 'z':
            event.preventDefault();
            if (event.shiftKey) {
              // Ctrl+Shift+Z or Cmd+Shift+Z for redo
              if (canRedo) {
                redo();
              }
            } else {
              // Ctrl+Z or Cmd+Z for undo
              if (canUndo) {
                undo();
              }
            }
            return;
          case 'y':
            event.preventDefault();
            // Ctrl+Y or Cmd+Y for redo (Windows style)
            if (canRedo) {
              redo();
            }
            return;
          case 'a':
            event.preventDefault();
            // Ctrl+A for select all elements (excluding elements that are part of a group)
            const allElementIds = projectStore.elements
              .filter(el => !el.groupId) // Don't select elements that are part of a group
              .map(el => el.id);
            projectStore.selectElements(allElementIds);
            return;
          case 'c':
            event.preventDefault();
            if (projectStore.selectedElementIds.size > 0) {
              projectStore.copySelected();
            }
            return;
          case 'v':
            event.preventDefault();
            event.stopPropagation(); // Prevent event bubbling
            // First check if we have copied canvas elements to paste
            if (projectStore.clipboard.length > 0) {
              // We have canvas elements in clipboard, paste them directly
              projectStore.paste();
            } else {
              // No canvas elements, try system clipboard (image, SVG, or text)
              handleClipboardPaste().then(success => {
                if (!success) {
                  // If system clipboard paste failed, still try regular paste as fallback
                  projectStore.paste();
                }
              });
            }
            return;
          case 'd':
            event.preventDefault();
            if (projectStore.selectedElementIds.size > 0) {
              projectStore.duplicateSelected();
            }
            return;
          case 'g':
            event.preventDefault();
            event.stopPropagation(); // Prevent any further event handling
            event.stopImmediatePropagation(); // Prevent other listeners on same element
            if (event.shiftKey) {
              // Ctrl+Shift+G for ungroup
              const selectedElements = projectStore.selectedElements;
              const groupElements = selectedElements.filter(
                el => el.type === 'group'
              );
              if (groupElements.length > 0) {
                groupElements.forEach(group => {
                  projectStore.ungroupElements(group.id);
                });
              }
            } else {
              // Ctrl+G for group
              if (projectStore.selectedElementIds.size >= 2) {
                const selectedIds = Array.from(
                  projectStore.selectedElementIds.keys()
                );
                projectStore.groupElements(selectedIds);
              }
            }
            return;
        }
      }

      // Handle regular key shortcuts (lower priority)
      switch (event.key) {
        case ' ':
          uiStore.setSpacebarPressed(true);
          break;
        case '1':
          uiStore.setActiveTool('select');
          break;
        case '2':
          uiStore.setActiveTool('rectangle');
          break;
        case '3':
          uiStore.setActiveTool('ellipse');
          break;
        case '4':
          uiStore.setActiveTool('diamond');
          break;
        case '5':
          uiStore.setActiveTool('arrow');
          break;
        case '6':
          uiStore.setActiveTool('line');
          break;
        case '7':
          uiStore.setActiveTool('text');
          break;
        case '8':
          uiStore.setActiveTool('icon');
          break;
        case '9':
          uiStore.setActiveTool('image');
          break;
        case 'Delete':
        case 'Backspace':
          if (projectStore.selectedElementIds.size > 0) {
            projectStore.deleteSelectedElements();
          }
          break;
        case 'Escape':
          if (uiStore.isDrawing) {
            // Cancel current drawing - this will be handled by tool slice
          } else if (uiStore.activeTool === 'select') {
            // Call the select tool's onCancel method to stop control point editing
            // const selectTool = toolManager.getTool('select') as SelectTool;
            // if (selectTool) {
            //   selectTool.onCancel({} as any);
            // }
            projectStore.clearSelection();
          } else {
            projectStore.clearSelection();
          }
          break;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === ' ') {
        event.preventDefault();
        uiStore.setSpacebarPressed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [projectStore, uiStore, canUndo, canRedo, undo, redo]);
};
